import {EventEmitter, Injectable} from '@angular/core';
import { SelectedClientModel } from '@models/selected-client.model';
import {CurrentUserModel} from '@models/current-user.model';
import {TBankAccountSource} from '@common/types';
import {NgOption} from '@ng-select/ng-select';
import {LS_MODAL_LOCALSTORAGE_PATH} from '@shared/ledger-sync-modal/constants';
import {AG_GRID_STATES_KEY} from '../../controls/ag-grid/constants';

const LANGUAGE_ITEM: string = 'app.lang';
const USER_DETAILS_ITEM: string = 'user.details';
const SELECTED_CLIENT_ITEM: string = 'app.selectedClient';
const SELECTED_STATEMENT: string = 'app.selectedStatement';

// !!!WARNING leave previous values of keys, to ensure backward compatibility
// const TRANSACTIONS_VIEW_COLUMNS = 'app.transactions.grid.cols';
const TRANSACTIONS_VIEW_COLUMNS = 'app.transactions.grid.cols-1';
// for history
// const BANK_ACCOUNT_SELECTOR_VALUE = 'app.selector.bank-n-account.value';
// const BANK_ACCOUNT_SELECTOR_VALUE = 'app.selector.bank-and-account.value';
const BANK_ACCOUNT_SELECTOR_VALUE = 'app.selector.bank-and-account.value1';
const TRANSACTIONS_PAGE_SIZE_FIELD = 'transactions.page.size';
const RULES_GRID_PAGESIZE = 'rules.page.size';
const COLLAPSED_FIRMS = 'app.sidebar.collapsed-firms';
const IDLE_LAST_EVENT_TIME = 'app.last-event-time';
// The "try the new design" offer was answered in this browser (see UiSwitchService).
const NEW_DESIGN_OFFERED = 'app.newDesignOffered';
const TYPING_ANIMATION = 'app.accounting.initial.typing-animation';
const TASK_CENTER_EVENTS = 'task-center.events';

const DONT_CLEAR = [
  'company',
  NEW_DESIGN_OFFERED,
  LS_MODAL_LOCALSTORAGE_PATH,
  LANGUAGE_ITEM,
  TRANSACTIONS_VIEW_COLUMNS,
  BANK_ACCOUNT_SELECTOR_VALUE,
  TRANSACTIONS_PAGE_SIZE_FIELD,
  RULES_GRID_PAGESIZE,
  AG_GRID_STATES_KEY,
  COLLAPSED_FIRMS,
  TYPING_ANIMATION,
  TASK_CENTER_EVENTS,
];

@Injectable({
    providedIn: 'root'
})
export class LocalStorageService {

    constructor() {
    }

    /**
     * set value to local storage
     * @param key   - local storage key
     * @param value - setting value
     */
    setValue(key, value) {
        localStorage.setItem(key, value);
    }

    /**
     * get value from local storage
     * @param key - local storage key
     */
    getValue(key) {
        return localStorage.getItem(key);
    }

    /**
     * remove value from local storage
     * @param key - local storage key
     */
    removeItem(key) {
        localStorage.removeItem(key);
    }

    isNewDesignOffered(): boolean {
        return this.hasValue(NEW_DESIGN_OFFERED);
    }

    setNewDesignOffered() {
        this.setValue(NEW_DESIGN_OFFERED, 'true');
    }

    /**
     * check if local storage has value
     * @param key - local storage key
     */
    hasValue(key) {
        return !!localStorage.getItem(key);
    }

    /**
     * clear local storage records
     */
    clearStorage() {
        const restore = {};
        DONT_CLEAR.forEach((item) => {
            const data = localStorage.getItem(item);
            if (data) {
                restore[item] = data;
            }
        });
        localStorage.clear();
        Object.keys(restore).forEach((item) => localStorage.setItem(item, restore[item]));
    }

    /**
     * set current user details
     * @param value - user details
     */
    setCurrentUserDetails(value) {
        localStorage.setItem(USER_DETAILS_ITEM, JSON.stringify(value));
    }

    /**
     * get current user details
     */
    getCurrentUserDetails(): CurrentUserModel {
        return JSON.parse(localStorage.getItem(USER_DETAILS_ITEM));
    }

    getCurrentUsername(): string {
        return this.getCurrentUserDetails()?.username;
    }

    /**
     * set selected client
     * @param details - selected client details
     */
    setSelectedClient(details) {
        localStorage.setItem(SELECTED_CLIENT_ITEM, JSON.stringify(details));
    }

    /**
     * get selected client details
     */
    getSelectedClient(): SelectedClientModel {
        return JSON.parse(localStorage.getItem(SELECTED_CLIENT_ITEM));
    }

    /**
     * get selected client username
     */
    getSelectedClientUsername(): string {
        return this.getSelectedClient()?.username;
    }

    /**
     * clear selected client data
     */
    clearSelectedClient() {
        localStorage.removeItem(SELECTED_CLIENT_ITEM);
    }


    /**
     * sets selected statement
     * @param details - selected statement {statementId}
     */
    setSelectedStatement(details: string) {
        localStorage.setItem(SELECTED_STATEMENT, JSON.stringify(details));
    }

    /**
     * get bank account details
     */
    getSelectedStatement() {
        return JSON.parse(localStorage.getItem(SELECTED_STATEMENT));
    }

    /**
     * set language
     * @param lang
     */
    setLang(lang) {
        localStorage.setItem(LANGUAGE_ITEM, JSON.stringify(lang));
    }

    /**
     * get language
     */
    getLang() {
        return JSON.parse(localStorage.getItem(LANGUAGE_ITEM));
    }

    /**
     * check if there is authentication details present
     */
    isAuthenticated(): boolean {
        return !!localStorage.getItem(USER_DETAILS_ITEM);
    }

    /**
     * check if user is anonymous
     */
    isAnonymous(): boolean {
        return !this.isAuthenticated();
    }

    /**
     * check if user has role
     * @param roles - user roles
     */
    hasRole(roles: string[]) {
        return this.isAuthenticated() && roles.includes(this.getCurrentUserDetails().role);
    }

    /**
     * check if user has zone
     * @param zones - permission codes
     */
    hasZone(zones: string[]) {
        const permissions = this.getCurrentUserDetails()?.permissions || [];
        return this.isAuthenticated() && zones?.some((zone) => permissions.includes(zone));
    }

    /**
     * refresh the current user's cached permission zones (e.g. from GET /permissions/me),
     * without touching the rest of the stored user details
     * @param permissions - permission codes
     */
    setPermissions(permissions: string[]) {
        const details = this.getCurrentUserDetails();
        if (!details) {
            return;
        }
        this.setCurrentUserDetails({...details, permissions});
    }

    setLastBankAccountRefreshDateTime(accountCode, dateTime) {
        localStorage.setItem(accountCode, JSON.stringify(dateTime));
    }

    getLastBankAccountRefreshDateTime(accountCode) {
        const lastBankAccountRefreshDateTime = localStorage.getItem(accountCode);
        return lastBankAccountRefreshDateTime !== null ? JSON.parse(lastBankAccountRefreshDateTime) : null;
    }

    setTransactionsViewColumns(columns: string[]) {
        const colsObj = localStorage.getItem(TRANSACTIONS_VIEW_COLUMNS);
        const selectedClient = this.getSelectedClientUsername();
        let toSave = {[selectedClient]: columns || null};
        if (colsObj) {
            const parsed = JSON.parse(colsObj);
            parsed[selectedClient] = columns || null;
            toSave = {...parsed};
        }

        localStorage.setItem(TRANSACTIONS_VIEW_COLUMNS, JSON.stringify(toSave));
    }

    getTransactionsViewColumns(): string[] {
        const selectedClient = this.getSelectedClientUsername();
        if (!selectedClient) {
            return null;
        }
        const result = localStorage.getItem(TRANSACTIONS_VIEW_COLUMNS);
        return result ? JSON.parse(result)[selectedClient] : null;
    }

    setBankAccountSelectorValue(value: NgOption, area: string) {
        if (!area) {
            // will not save value if area is not provided
            return;
        }
        area = area + this.getCurrentUsername();
        const lsValue = localStorage.getItem(BANK_ACCOUNT_SELECTOR_VALUE);
        const result = lsValue ? JSON.parse(lsValue) : {};
        localStorage.setItem(BANK_ACCOUNT_SELECTOR_VALUE, JSON.stringify({
            ...result,
            [this.getSelectedClientUsername()]: {
                ...(result[this.getSelectedClientUsername()] || {}),
                [area]: value,
            }
        }));
    }

    getBankAccountSelectorValue(area: string, username?: string): NgOption {
        const result = localStorage.getItem(BANK_ACCOUNT_SELECTOR_VALUE);
        if (result) {
            const parsed = JSON.parse(result);
            area = area + this.getCurrentUsername();
            return parsed[username || this.getSelectedClientUsername()]?.[area] || null;
        }

        return null;
    }

    setTransactionsPagesize(pageSize) {
        localStorage.setItem(TRANSACTIONS_PAGE_SIZE_FIELD, pageSize);
    }

    getTransactionsPageSize(): number {
        return +localStorage.getItem(TRANSACTIONS_PAGE_SIZE_FIELD);
    }

    // todo move transactions to abstract pagesize save
    setRulesPagesize(pageSize: number, what: 'global' | 'client') {
        const data = localStorage.getItem(RULES_GRID_PAGESIZE);
        let parsedData = {};
        try {
            parsedData = JSON.parse(data);
        } catch {
            // nothing
        }
        localStorage.setItem(RULES_GRID_PAGESIZE, JSON.stringify({
            ...parsedData,
            [what]: pageSize,
        }));
    }

    getRulesPageSize(what: 'global' | 'client'): number {
        const data = localStorage.getItem(RULES_GRID_PAGESIZE);
        let parsedData = {};
        try {
            parsedData = JSON.parse(data);
        } catch {
            // nothing
        }

        return parsedData?.[what] || 20;
    }

    getCollapsedFirms() {
        return (JSON.parse(localStorage.getItem(COLLAPSED_FIRMS)) || {})[this.getCurrentUsername()] || [];
    }

    saveCollapsedFirms(collapsedFirms: number[]) {
        localStorage.setItem(COLLAPSED_FIRMS, JSON.stringify({
            ...(JSON.parse(localStorage.getItem(COLLAPSED_FIRMS)) || {}),
            [this.getCurrentUsername()]: collapsedFirms,
        }))
    }

    setLastEventTime() {
        localStorage.setItem(IDLE_LAST_EVENT_TIME, (new Date().getTime()).toString());
    }

    getLastEventTime() {
        const lastEventTime = localStorage.getItem(IDLE_LAST_EVENT_TIME);
        return lastEventTime && parseInt(lastEventTime, 10);
    }

    getTypingAnimation() {
        return localStorage.getItem(TYPING_ANIMATION);
    }

    setTypingAnimation(how: boolean) {
        return localStorage.setItem(TYPING_ANIMATION, how.toString());
    }
}
